import type { ShopProduct } from '../mocks/shopProducts';
import { fetchFunctionsApi } from '../api';
import { getApiErrorMessage, parseApiResponse } from '../security';
import { buildShopProducts, extractRemoteItems, getRemoteCatalogItemId } from './adminShopProducts';
import { extractStyleCodesFromItemsPayload } from './itemStyleCodes';
import type { ItemSizingType } from './sizingConventions';
import type { ItemSeason } from './itemSeasons';
import type { ItemOccasion } from './itemOccasions';

type RemoteCatalogItem = Record<string, unknown>;
export interface RemoteCatalogRequestFilters {
  gender?: 'male' | 'female' | 'unisex';
  genders?: Array<'male' | 'female' | 'unisex'>;
  category?: string;
  categories?: string[];
  subcategory?: string;
  subcategory2?: string;
  material?: string;
  materials?: string[];
  season?: ItemSeason;
  seasons?: ItemSeason[];
  allSeason?: boolean;
  occasion?: ItemOccasion;
  style?: string;
  styles?: string[];
  customSizing?: boolean;
  inStockOnly?: boolean;
  search?: string;
  sort?: 'price-asc' | 'price-desc';
}

export interface RemoteCatalogFilterCountGroup {
  total?: Record<string, number>;
  category: Record<string, number>;
  customCategory: Record<string, number>;
  standardCategory: Record<string, number>;
  subcategory: Record<string, number>;
  customSubcategory: Record<string, number>;
  standardSubcategory: Record<string, number>;
  customSubcategoryByCategory: Record<string, Record<string, number>>;
  standardSubcategoryByCategory: Record<string, Record<string, number>>;
  hasCategoryScopedSubcategory: boolean;
  hasCustomSubcategory: boolean;
  hasStandardSubcategory: boolean;
  subcategory2: Record<string, number>;
  customSubcategory2: Record<string, number>;
  standardSubcategory2: Record<string, number>;
  hasCustomSubcategory2: boolean;
  hasStandardSubcategory2: boolean;
  material: Record<string, number>;
  customMaterial: Record<string, number>;
  standardMaterial: Record<string, number>;
  season?: Record<string, number>;
  customSeason?: Record<string, number>;
  standardSeason?: Record<string, number>;
  occasion?: Record<string, number>;
  customOccasion?: Record<string, number>;
  standardOccasion?: Record<string, number>;
  customSizing: Record<string, number>;
}

export type RemoteCatalogFilterGender = 'female' | 'male' | 'unisex';

export type RemoteCatalogFilterCounts = Partial<
  Record<RemoteCatalogFilterGender, RemoteCatalogFilterCountGroup>
>;

export function getRemoteCatalogGenderCount(
  filterCounts: RemoteCatalogFilterCounts,
  gender: RemoteCatalogFilterGender,
  customSizing?: boolean
) {
  const group = filterCounts[gender];
  if (!group) {
    return null;
  }

  if (group.total && Object.keys(group.total).length > 0) {
    if (typeof customSizing === 'boolean') {
      const count = group.total[String(customSizing)];
      return typeof count === 'number' ? count : null;
    }

    const counts = ['true', 'false']
      .map((key) => group.total?.[key])
      .filter((count): count is number => typeof count === 'number');
    return counts.length > 0
      ? counts.reduce((total, count) => total + count, 0)
      : null;
  }

  const categoryCounts = getRemoteCatalogCategoryCounts(group, customSizing);
  const categoryValues = Object.values(categoryCounts);
  if (categoryValues.length > 0) {
    return categoryValues.reduce((total, count) => total + count, 0);
  }

  const sizingCounts = group.customSizing;
  if (typeof customSizing === 'boolean') {
    const count = sizingCounts[String(customSizing)];
    return typeof count === 'number' ? count : null;
  }

  const counts = ['true', 'false']
    .map((key) => sizingCounts[key])
    .filter((count): count is number => typeof count === 'number');
  return counts.length > 0
    ? counts.reduce((total, count) => total + count, 0)
    : null;
}

export function getRemoteCatalogCategoryCounts(
  group: RemoteCatalogFilterCountGroup,
  customSizing?: boolean
) {
  if (customSizing === true && Object.keys(group.customCategory).length > 0) {
    return group.customCategory;
  }
  if (customSizing === false && Object.keys(group.standardCategory).length > 0) {
    return group.standardCategory;
  }
  if (
    typeof customSizing === 'undefined'
    && (Object.keys(group.customCategory).length > 0 || Object.keys(group.standardCategory).length > 0)
  ) {
    const combinedCounts = { ...group.customCategory };
    Object.entries(group.standardCategory).forEach(([category, count]) => {
      combinedCounts[category] = (combinedCounts[category] ?? 0) + count;
    });
    return combinedCounts;
  }
  return group.category;
}

export function getRemoteCatalogMaterialCounts(
  group: RemoteCatalogFilterCountGroup,
  customSizing?: boolean,
) {
  if (customSizing === true && Object.keys(group.customMaterial).length > 0) {
    return group.customMaterial;
  }
  if (customSizing === false && Object.keys(group.standardMaterial).length > 0) {
    return group.standardMaterial;
  }
  if (
    typeof customSizing === 'undefined'
    && (Object.keys(group.customMaterial).length > 0 || Object.keys(group.standardMaterial).length > 0)
  ) {
    return mergeRemoteCatalogCountMaps(group.customMaterial, group.standardMaterial);
  }
  return group.material;
}

function mergeRemoteCatalogCountMaps(
  primary: Record<string, number>,
  secondary: Record<string, number>,
) {
  const merged = { ...primary };
  Object.entries(secondary).forEach(([key, count]) => {
    merged[key] = (merged[key] ?? 0) + count;
  });
  return merged;
}

export function getRemoteCatalogSubcategoryCounts(
  group: RemoteCatalogFilterCountGroup,
  customSizing?: boolean,
  level: 1 | 2 = 1,
  category?: string,
) {
  const isSecondLevel = level === 2;
  if (!isSecondLevel && category && !group.hasCategoryScopedSubcategory) {
    return {};
  }
  if (!isSecondLevel && category && group.hasCategoryScopedSubcategory) {
    const normalizedCategory = category.trim().toLowerCase();
    const byCategory = customSizing === true
      ? group.customSubcategoryByCategory
      : customSizing === false
        ? group.standardSubcategoryByCategory
        : {
          ...group.customSubcategoryByCategory,
          ...Object.fromEntries(
            Object.entries(group.standardSubcategoryByCategory).map(([key, counts]) => [
              key,
              mergeRemoteCatalogCountMaps(group.customSubcategoryByCategory[key] ?? {}, counts),
            ])
          ),
        };
    const matchingCategory = Object.keys(byCategory).find((key) => key.toLowerCase() === normalizedCategory);
    return matchingCategory ? byCategory[matchingCategory] : {};
  }
  const combined = isSecondLevel ? group.subcategory2 : group.subcategory;
  const custom = isSecondLevel ? group.customSubcategory2 : group.customSubcategory;
  const standard = isSecondLevel ? group.standardSubcategory2 : group.standardSubcategory;
  const hasCustom = isSecondLevel ? group.hasCustomSubcategory2 : group.hasCustomSubcategory;
  const hasStandard = isSecondLevel ? group.hasStandardSubcategory2 : group.hasStandardSubcategory;

  if (customSizing === true) {
    return hasCustom || hasStandard ? custom : combined;
  }
  if (customSizing === false) {
    return hasCustom || hasStandard ? standard : combined;
  }
  return hasCustom || hasStandard
    ? mergeRemoteCatalogCountMaps(custom, standard)
    : combined;
}

export function getRemoteCatalogSeasonCounts(
  group: RemoteCatalogFilterCountGroup,
  customSizing?: boolean,
) {
  if (customSizing === true) {
    return group.customSeason ?? group.season ?? {};
  }
  if (customSizing === false) {
    return group.standardSeason ?? group.season ?? {};
  }
  return group.season ?? mergeRemoteCatalogCountMaps(
    group.customSeason ?? {},
    group.standardSeason ?? {},
  );
}

export function getRemoteCatalogOccasionCounts(
  group: RemoteCatalogFilterCountGroup,
  customSizing?: boolean,
) {
  if (customSizing === true) {
    return group.customOccasion ?? group.occasion ?? {};
  }
  if (customSizing === false) {
    return group.standardOccasion ?? group.occasion ?? {};
  }
  return group.occasion ?? mergeRemoteCatalogCountMaps(
    group.customOccasion ?? {},
    group.standardOccasion ?? {},
  );
}

type RemoteCatalogListingCache = {
  adminProducts: ShopProduct[];
  availableItemIds: string[];
  remoteItems: RemoteCatalogItem[];
  styleCodeOptionValues: string[];
  loadedOffsets: number[];
  nextOffset: number;
  hasMore: boolean;
  totalCount: number | null;
  filterCounts: RemoteCatalogFilterCounts | null;
  limit: number;
  filterKey: string;
};

export interface RemoteCatalogSnapshot {
  adminProducts: ShopProduct[];
  availableItemIds: string[];
  remoteItems: RemoteCatalogItem[];
  styleCodeOptionValues: string[];
}

export interface RemoteCatalogPageSnapshot {
  adminProducts: ShopProduct[];
  availableItemIds: string[];
  remoteItems: RemoteCatalogItem[];
  styleCodeOptionValues: string[];
  offset: number;
  limit: number;
  hasMore: boolean;
  totalCount: number | null;
  filterCounts: RemoteCatalogFilterCounts | null;
  filterKey?: string;
}

let remoteCatalogSnapshotCache: RemoteCatalogSnapshot | null = null;
let remoteCatalogSnapshotPromise: Promise<RemoteCatalogSnapshot> | null = null;
let remoteCatalogProductCache = new Map<string, ShopProduct>();
const remoteCatalogProductPromiseById = new Map<string, Promise<ShopProduct | null>>();
const remoteCatalogListingCacheByLimit = new Map<string, RemoteCatalogListingCache>();
const remoteCatalogPagePromiseByKey = new Map<string, Promise<RemoteCatalogPageSnapshot>>();

function appendRemoteCatalogFilterParams(
  searchParams: URLSearchParams,
  filters?: RemoteCatalogRequestFilters,
) {
  if (filters?.genders?.length) {
    searchParams.set('genders', filters.genders.join(','));
  } else if (filters?.gender) {
    searchParams.set('gender', filters.gender);
  }

  if (filters?.categories?.length) {
    searchParams.set('categories', filters.categories.join(','));
  } else if (filters?.category) {
    searchParams.set('category', filters.category);
  }
  if (filters?.subcategory) {
    searchParams.set('item_subcategory', filters.subcategory);
  }
  if (filters?.subcategory2) {
    searchParams.set('item_subcategory2', filters.subcategory2);
  }
  if (filters?.materials?.length) {
    searchParams.set('materials', filters.materials.join(','));
  } else if (filters?.material) {
    searchParams.set('material', filters.material);
  }
  if (filters?.seasons?.length) {
    searchParams.set('season', filters.seasons.join(','));
  } else if (filters?.season) {
    searchParams.set('season', filters.season);
  } else if (filters?.allSeason) {
    searchParams.set('all_season', 'true');
  }
  if (filters?.occasion) {
    searchParams.set('occasion', filters.occasion);
  }
  if (filters?.styles?.length) {
    searchParams.set('style', filters.styles.join(','));
  } else if (filters?.style) {
    searchParams.set('style', filters.style);
  }
  if (typeof filters?.customSizing === 'boolean') {
    searchParams.set('custom_sizing', String(filters.customSizing));
  }
  if (filters?.inStockOnly) {
    searchParams.set('in_stock_items_only', 'true');
  }
  if (filters?.search) {
    searchParams.set('search', filters.search);
  }
  if (filters?.sort) {
    searchParams.set('sort', filters.sort === 'price-asc' ? 'price_asc' : 'price_desc');
  }
}

function buildRemoteCatalogFilterKey(filters?: RemoteCatalogRequestFilters) {
  const searchParams = new URLSearchParams();
  appendRemoteCatalogFilterParams(searchParams, filters);
  return searchParams.toString();
}

function buildRemoteCatalogListingCacheKey(limit: number, filters?: RemoteCatalogRequestFilters) {
  return buildRemoteCatalogListingCacheKeyFromFilterKey(limit, buildRemoteCatalogFilterKey(filters));
}

function buildRemoteCatalogListingCacheKeyFromFilterKey(limit: number, filterKey: string) {
  return `${limit}:${filterKey}`;
}

function cacheCatalogProducts(products: ShopProduct[]) {
  products.forEach((product) => {
    if (product.id) {
      remoteCatalogProductCache.set(product.id, product);
    }
  });
}

function findMatchingCatalogProduct(itemId: string, products: ShopProduct[]) {
  return products.find((product) =>
    product.id === itemId
    || product.id.startsWith(itemId)
    || itemId.startsWith(product.id)
  ) ?? null;
}

function rebuildCatalogProducts(
  remoteItems: RemoteCatalogItem[],
  sizingOptionsByType: Map<ItemSizingType, string[]>
) {
  const nextProducts = buildShopProducts(remoteItems, sizingOptionsByType);
  cacheCatalogProducts(nextProducts);
  return nextProducts;
}

function buildRemoteCatalogSnapshot(payload: unknown): RemoteCatalogSnapshot {
  const remoteItems = extractRemoteItems(payload);
  const adminProducts = buildShopProducts(remoteItems);

  cacheCatalogProducts(adminProducts);

  return {
    adminProducts,
    availableItemIds: Array.from(
      new Set(
        remoteItems
          .map((entry) => getRemoteCatalogItemId(entry))
          .filter(Boolean)
      )
    ),
    remoteItems,
    styleCodeOptionValues: extractStyleCodesFromItemsPayload(payload),
  };
}

function normalizeNonNegativeInteger(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.trunc(value));
  }

  if (typeof value === 'string') {
    const parsedValue = Number(value);
    if (Number.isFinite(parsedValue)) {
      return Math.max(0, Math.trunc(parsedValue));
    }
  }

  return null;
}

function resolveRemoteCatalogPageTotalCount(payload: unknown) {
  if (!payload || typeof payload !== 'object' || !('pagination' in payload)) {
    return null;
  }

  const pagination = (payload as { pagination?: unknown }).pagination;
  if (!pagination || typeof pagination !== 'object') {
    return null;
  }

  return normalizeNonNegativeInteger((pagination as { total?: unknown }).total);
}

function normalizeCountMap(value: unknown) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value)
      .map(([key, count]) => [key, normalizeNonNegativeInteger(count)] as const)
      .filter((entry): entry is readonly [string, number] => entry[1] !== null)
  );
}

function normalizeSplitCountMaps(value: unknown) {
  const record = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
  const hasCustom = Object.prototype.hasOwnProperty.call(record, 'true');
  const hasStandard = Object.prototype.hasOwnProperty.call(record, 'false');
  const custom = normalizeCountMap(record.true);
  const standard = normalizeCountMap(record.false);
  const combined = hasCustom || hasStandard
    ? mergeRemoteCatalogCountMaps(custom, standard)
    : normalizeCountMap(value);

  return { combined, custom, standard, hasCustom, hasStandard };
}

function normalizeNestedCountMap(value: unknown) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value)
      .map(([category, counts]) => [category, normalizeCountMap(counts)] as const)
      .filter((entry) => Object.keys(entry[1]).length > 0)
  );
}

function mergeNestedCountMaps(
  primary: Record<string, Record<string, number>>,
  secondary: Record<string, Record<string, number>>,
) {
  const merged: Record<string, Record<string, number>> = {};
  Object.entries(primary).forEach(([category, counts]) => {
    merged[category] = { ...counts };
  });
  Object.entries(secondary).forEach(([category, counts]) => {
    merged[category] = mergeRemoteCatalogCountMaps(merged[category] ?? {}, counts);
  });
  return merged;
}

function flattenNestedCountMaps(value: Record<string, Record<string, number>>) {
  return Object.values(value).reduce<Record<string, number>>(
    (combined, counts) => mergeRemoteCatalogCountMaps(combined, counts),
    {}
  );
}

function normalizeSizingCountMaps(value: unknown) {
  const record = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
  const hasCustomSizing = Object.prototype.hasOwnProperty.call(record, 'custom_sizing');
  const hasStandardSizing = Object.prototype.hasOwnProperty.call(record, 'standard_sizing');

  if (hasCustomSizing || hasStandardSizing) {
    const custom = normalizeCountMap(record.custom_sizing);
    const standard = normalizeCountMap(record.standard_sizing);
    return {
      combined: mergeRemoteCatalogCountMaps(custom, standard),
      custom,
      standard,
      hasCustom: hasCustomSizing,
      hasStandard: hasStandardSizing,
    };
  }

  return normalizeSplitCountMaps(value);
}

function normalizeCategorySubcategoryMaps(value: unknown) {
  const record = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
  const hasCustomSizing = Object.prototype.hasOwnProperty.call(record, 'custom_sizing');
  const hasStandardSizing = Object.prototype.hasOwnProperty.call(record, 'standard_sizing');

  if (hasCustomSizing || hasStandardSizing) {
    const customByCategory = normalizeNestedCountMap(record.custom_sizing);
    const standardByCategory = normalizeNestedCountMap(record.standard_sizing);
    return {
      combined: flattenNestedCountMaps(mergeNestedCountMaps(customByCategory, standardByCategory)),
      custom: flattenNestedCountMaps(customByCategory),
      standard: flattenNestedCountMaps(standardByCategory),
      customByCategory,
      standardByCategory,
      hasCustom: hasCustomSizing,
      hasStandard: hasStandardSizing,
      hasCategoryScoped: true,
    };
  }

  const split = normalizeSplitCountMaps(value);
  return {
    ...split,
    customByCategory: {},
    standardByCategory: {},
    hasCategoryScoped: false,
  };
}

function resolveRemoteCatalogFilterCounts(payload: unknown): RemoteCatalogFilterCounts | null {
  if (!payload || typeof payload !== 'object' || !('filter_counts' in payload)) {
    return null;
  }

  const rawFilterCounts = (payload as { filter_counts?: unknown }).filter_counts;
  if (!rawFilterCounts || typeof rawFilterCounts !== 'object' || Array.isArray(rawFilterCounts)) {
    return null;
  }

  const supportedGenders = new Set<RemoteCatalogFilterGender>(['female', 'male', 'unisex']);
  const filterCounts = Object.fromEntries(
    Object.entries(rawFilterCounts).flatMap(([rawGender, rawGroup]) => {
      const gender = rawGender.trim().toLowerCase() as RemoteCatalogFilterGender;
      if (!supportedGenders.has(gender)) {
        return [];
      }
      const group = rawGroup && typeof rawGroup === 'object' && !Array.isArray(rawGroup)
        ? rawGroup as Record<string, unknown>
        : {};
      const rawCategory = group.category && typeof group.category === 'object' && !Array.isArray(group.category)
        ? group.category as Record<string, unknown>
        : {};
      const subcategoryCounts = normalizeCategorySubcategoryMaps(group.subcategory);
      const subcategory2Counts = normalizeSizingCountMaps(group.subcategory2);
      const materialCounts = normalizeSizingCountMaps(group.material);
      const seasonCounts = normalizeSizingCountMaps(group.season);
      const occasionCounts = normalizeSizingCountMaps(group.occasion);
      return [[gender, {
        ...(Object.prototype.hasOwnProperty.call(group, 'total') ? {
          total: normalizeCountMap(group.total),
        } : {}),
        category: normalizeCountMap(rawCategory),
        customCategory: normalizeCountMap(rawCategory.custom_sizing),
        standardCategory: normalizeCountMap(rawCategory.standard_sizing),
        subcategory: subcategoryCounts.combined,
        customSubcategory: subcategoryCounts.custom,
        standardSubcategory: subcategoryCounts.standard,
        customSubcategoryByCategory: subcategoryCounts.customByCategory,
        standardSubcategoryByCategory: subcategoryCounts.standardByCategory,
        hasCategoryScopedSubcategory: subcategoryCounts.hasCategoryScoped,
        hasCustomSubcategory: subcategoryCounts.hasCustom,
        hasStandardSubcategory: subcategoryCounts.hasStandard,
        subcategory2: subcategory2Counts.combined,
        customSubcategory2: subcategory2Counts.custom,
        standardSubcategory2: subcategory2Counts.standard,
        hasCustomSubcategory2: subcategory2Counts.hasCustom,
        hasStandardSubcategory2: subcategory2Counts.hasStandard,
        material: materialCounts.combined,
        customMaterial: materialCounts.custom,
        standardMaterial: materialCounts.standard,
        ...(Object.prototype.hasOwnProperty.call(group, 'season') ? {
          season: seasonCounts.combined,
          customSeason: seasonCounts.custom,
          standardSeason: seasonCounts.standard,
        } : {}),
        ...(Object.prototype.hasOwnProperty.call(group, 'occasion') ? {
          occasion: occasionCounts.combined,
          customOccasion: occasionCounts.custom,
          standardOccasion: occasionCounts.standard,
        } : {}),
        customSizing: normalizeCountMap(group.custom_sizing),
      }]];
    })
  ) as RemoteCatalogFilterCounts;

  return Object.keys(filterCounts).length > 0 ? filterCounts : null;
}

function buildRemoteCatalogPageSnapshot(payload: unknown, limit: number, offset: number): RemoteCatalogPageSnapshot {
  const remoteItems = extractRemoteItems(payload);
  return buildRemoteCatalogPageSnapshotFromItems({
    remoteItems,
    styleCodeOptionValues: extractStyleCodesFromItemsPayload(payload),
    offset,
    limit,
    totalCount: resolveRemoteCatalogPageTotalCount(payload),
    filterCounts: resolveRemoteCatalogFilterCounts(payload),
  });
}

function buildRemoteCatalogPageSnapshotFromItems(options: {
  remoteItems: RemoteCatalogItem[];
  styleCodeOptionValues: string[];
  offset: number;
  limit: number;
  totalCount: number | null;
  filterCounts: RemoteCatalogFilterCounts | null;
}): RemoteCatalogPageSnapshot {
  const { remoteItems, offset, limit, totalCount, filterCounts } = options;
  const adminProducts = buildShopProducts(remoteItems);

  cacheCatalogProducts(adminProducts);

  return {
    adminProducts,
    availableItemIds: Array.from(
      new Set(
        remoteItems
          .map((entry) => getRemoteCatalogItemId(entry))
          .filter(Boolean)
      )
    ),
    remoteItems,
    styleCodeOptionValues: options.styleCodeOptionValues,
    offset,
    limit,
    hasMore: totalCount === null ? remoteItems.length >= limit : offset + remoteItems.length < totalCount,
    totalCount,
    filterCounts,
  };
}

function mergeRemoteCatalogListingCache(page: RemoteCatalogPageSnapshot) {
  const nextFilterKey = page.filterKey ?? '';
  const cacheKey = buildRemoteCatalogListingCacheKeyFromFilterKey(page.limit, nextFilterKey);
  const activeListing = remoteCatalogListingCacheByLimit.get(cacheKey);
  if (activeListing && activeListing.loadedOffsets.includes(page.offset)) {
    return activeListing;
  }

  const mergedProducts = [
    ...(activeListing?.adminProducts ?? []),
    ...page.adminProducts,
  ].reduce<ShopProduct[]>((accumulator, product) => {
    if (accumulator.some((entry) => entry.id === product.id)) {
      return accumulator;
    }

    accumulator.push(product);
    return accumulator;
  }, []);

  const mergedAvailableItemIds = Array.from(
    new Set([...(activeListing?.availableItemIds ?? []), ...page.availableItemIds])
  );
  const mergedRemoteItems = [
    ...(activeListing?.remoteItems ?? []),
    ...page.remoteItems,
  ].reduce<RemoteCatalogItem[]>((accumulator, item) => {
    const itemId = getRemoteCatalogItemId(item);
    if (itemId && accumulator.some((entry) => getRemoteCatalogItemId(entry) === itemId)) {
      return accumulator;
    }

    accumulator.push(item);
    return accumulator;
  }, []);
  const mergedStyleCodeOptionValues = Array.from(
    new Set([...(activeListing?.styleCodeOptionValues ?? []), ...page.styleCodeOptionValues])
  ).sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base', numeric: true }));
  const mergedLoadedOffsets = Array.from(
    new Set([...(activeListing?.loadedOffsets ?? []), page.offset])
  ).sort((left, right) => left - right);
  const totalCount = page.totalCount ?? activeListing?.totalCount ?? null;
  const filterCounts = page.filterCounts ?? activeListing?.filterCounts ?? null;
  const nextOffset = Math.max(activeListing?.nextOffset ?? 0, page.offset + page.remoteItems.length);
  const nextListing: RemoteCatalogListingCache = {
    adminProducts: mergedProducts,
    availableItemIds: mergedAvailableItemIds,
    remoteItems: mergedRemoteItems,
    styleCodeOptionValues: mergedStyleCodeOptionValues,
    loadedOffsets: mergedLoadedOffsets,
    nextOffset,
    hasMore: totalCount === null ? page.hasMore : nextOffset < totalCount,
    totalCount,
    filterCounts,
    limit: page.limit,
    filterKey: nextFilterKey,
  };

  remoteCatalogListingCacheByLimit.set(cacheKey, nextListing);
  return nextListing;
}

async function fetchRemoteCatalogSnapshot(errorMessage: string) {
  const response = await fetchFunctionsApi('/api/add_items');
  const payload = await parseApiResponse(response);

  if (!response.ok) {
    throw new Error(getApiErrorMessage(payload, errorMessage));
  }

  const snapshot = buildRemoteCatalogSnapshot(payload);
  remoteCatalogSnapshotCache = snapshot;
  return snapshot;
}

async function fetchRemoteCatalogPage(
  limit: number,
  offset: number,
  errorMessage: string,
  filters?: RemoteCatalogRequestFilters
) {
  const remoteItems: RemoteCatalogItem[] = [];
  const styleCodeOptionValues = new Set<string>();
  let totalCount: number | null = null;
  let filterCounts: RemoteCatalogFilterCounts | null = null;

  // Keep advancing by the number of rows actually returned if a partial
  // response still reports more matching catalog rows.
  while (remoteItems.length < limit) {
    const requestOffset = offset + remoteItems.length;
    const requestLimit = limit - remoteItems.length;
    const searchParams = new URLSearchParams({
      limit: String(requestLimit),
      offset: String(requestOffset),
    });
    appendRemoteCatalogFilterParams(searchParams, filters);

    const response = await fetchFunctionsApi(`/api/add_items?${searchParams.toString()}`);
    const payload = await parseApiResponse(response);

    if (!response.ok) {
      throw new Error(getApiErrorMessage(payload, errorMessage));
    }

    const responseSnapshot = buildRemoteCatalogPageSnapshot(payload, requestLimit, requestOffset);
    remoteItems.push(...responseSnapshot.remoteItems.slice(0, requestLimit));
    responseSnapshot.styleCodeOptionValues.forEach((value) => styleCodeOptionValues.add(value));
    totalCount = responseSnapshot.totalCount ?? totalCount;
    filterCounts = responseSnapshot.filterCounts ?? filterCounts;

    if (
      responseSnapshot.remoteItems.length === 0
      || (totalCount !== null && offset + remoteItems.length >= totalCount)
    ) {
      break;
    }
  }

  return {
    ...buildRemoteCatalogPageSnapshotFromItems({
      remoteItems,
      styleCodeOptionValues: Array.from(styleCodeOptionValues),
      offset,
      limit,
      totalCount,
      filterCounts,
    }),
    filterKey: buildRemoteCatalogFilterKey(filters),
  };
}

export function readCachedRemoteCatalogSnapshot() {
  return remoteCatalogSnapshotCache;
}

export function readCachedRemoteCatalogListing(limit: number, filters?: RemoteCatalogRequestFilters) {
  return remoteCatalogListingCacheByLimit.get(buildRemoteCatalogListingCacheKey(limit, filters)) ?? null;
}

export async function loadRemoteCatalogSnapshot(options?: {
  errorMessage?: string;
  forceRefresh?: boolean;
}) {
  const forceRefresh = options?.forceRefresh === true;
  const errorMessage = options?.errorMessage ?? 'Unable to load store items.';

  if (!forceRefresh && remoteCatalogSnapshotCache) {
    return remoteCatalogSnapshotCache;
  }

  if (!forceRefresh && remoteCatalogSnapshotPromise) {
    return remoteCatalogSnapshotPromise;
  }

  const nextRequest = fetchRemoteCatalogSnapshot(errorMessage);
  remoteCatalogSnapshotPromise = nextRequest;

  try {
    return await nextRequest;
  } finally {
    if (remoteCatalogSnapshotPromise === nextRequest) {
      remoteCatalogSnapshotPromise = null;
    }
  }
}

export async function loadRemoteCatalogProductById(
  itemId: string | undefined,
  options?: {
    errorMessage?: string;
    sizingOptionsByType?: Map<ItemSizingType, string[]>;
    forceRefresh?: boolean;
  }
) {
  const normalizedItemId = typeof itemId === 'string' ? itemId.trim() : '';
  if (!normalizedItemId) {
    return null;
  }

  if (!options?.forceRefresh) {
    const cachedProduct = findRemoteCatalogProductById(normalizedItemId);
    if (cachedProduct) {
      return cachedProduct;
    }
  }

  const existingRequest = remoteCatalogProductPromiseById.get(normalizedItemId);
  if (existingRequest) {
    return existingRequest;
  }

  const errorMessage = options?.errorMessage ?? 'Unable to load store items.';
  const nextRequest = (async () => {
    const response = await fetchFunctionsApi(`/api/add_items?item_id=${encodeURIComponent(normalizedItemId)}`);
    const payload = await parseApiResponse(response);

    if (!response.ok) {
      throw new Error(getApiErrorMessage(payload, errorMessage));
    }

    const fetchedProducts = buildShopProducts(
      extractRemoteItems(payload),
      options?.sizingOptionsByType
    );
    cacheCatalogProducts(fetchedProducts);

    return findMatchingCatalogProduct(normalizedItemId, fetchedProducts);
  })();
  remoteCatalogProductPromiseById.set(normalizedItemId, nextRequest);

  try {
    return await nextRequest;
  } finally {
    if (remoteCatalogProductPromiseById.get(normalizedItemId) === nextRequest) {
      remoteCatalogProductPromiseById.delete(normalizedItemId);
    }
  }
}

export async function loadRemoteCatalogProductsByIds(
  itemIds: Array<string | undefined>,
  options?: {
    errorMessage?: string;
    sizingOptionsByType?: Map<ItemSizingType, string[]>;
    forceRefresh?: boolean;
  }
) {
  const normalizedItemIds = Array.from(
    new Set(
      itemIds
        .map((itemId) => (typeof itemId === 'string' ? itemId.trim() : ''))
        .filter(Boolean)
    )
  );
  const productByRequestedId = new Map<string, ShopProduct | null>();
  const itemIdsToFetch = normalizedItemIds.filter((itemId) => {
    if (options?.forceRefresh) {
      return true;
    }

    const cachedProduct = findRemoteCatalogProductById(itemId);
    if (cachedProduct) {
      productByRequestedId.set(itemId, cachedProduct);
      return false;
    }

    return true;
  });

  if (itemIdsToFetch.length > 0) {
    const errorMessage = options?.errorMessage ?? 'Unable to load store items.';
    const searchParams = new URLSearchParams({
      item_ids: itemIdsToFetch.join(','),
    });
    const response = await fetchFunctionsApi(`/api/add_items?${searchParams.toString()}`);
    const payload = await parseApiResponse(response);

    if (!response.ok) {
      if (response.status !== 404) {
        throw new Error(getApiErrorMessage(payload, errorMessage));
      }
    }

    const fetchedProducts = response.ok
      ? buildShopProducts(
          extractRemoteItems(payload),
          options?.sizingOptionsByType
        )
      : [];
    cacheCatalogProducts(fetchedProducts);

    itemIdsToFetch.forEach((itemId) => {
      productByRequestedId.set(
        itemId,
        findMatchingCatalogProduct(itemId, fetchedProducts)
      );
    });
  }

  return normalizedItemIds
    .map((itemId) => productByRequestedId.get(itemId) ?? null)
    .filter((product): product is ShopProduct => product !== null);
}

export async function refreshRemoteCatalogSnapshot(errorMessage?: string) {
  return loadRemoteCatalogSnapshot({
    forceRefresh: true,
    errorMessage,
  });
}

export function clearRemoteCatalogSnapshotCache() {
  remoteCatalogSnapshotCache = null;
  remoteCatalogSnapshotPromise = null;
  remoteCatalogProductCache = new Map<string, ShopProduct>();
  remoteCatalogProductPromiseById.clear();
  remoteCatalogListingCacheByLimit.clear();
  remoteCatalogPagePromiseByKey.clear();
}

export function rehydrateRemoteCatalogProducts(
  sizingOptionsByType: Map<ItemSizingType, string[]>,
  options?: { forceRebuild?: boolean }
) {
  if (sizingOptionsByType.size <= 0 && !options?.forceRebuild) {
    return {
      snapshot: remoteCatalogSnapshotCache,
      listings: remoteCatalogListingCacheByLimit,
    };
  }

  remoteCatalogProductCache = new Map<string, ShopProduct>();

  if (remoteCatalogSnapshotCache) {
    remoteCatalogSnapshotCache = {
      ...remoteCatalogSnapshotCache,
      adminProducts: rebuildCatalogProducts(remoteCatalogSnapshotCache.remoteItems, sizingOptionsByType),
    };
  }

  Array.from(remoteCatalogListingCacheByLimit.entries()).forEach(([limit, listing]) => {
    remoteCatalogListingCacheByLimit.set(limit, {
      ...listing,
      adminProducts: rebuildCatalogProducts(listing.remoteItems, sizingOptionsByType),
    });
  });

  return {
    snapshot: remoteCatalogSnapshotCache,
    listings: remoteCatalogListingCacheByLimit,
  };
}

export async function loadRemoteCatalogPage(options: {
  limit: number;
  offset: number;
  errorMessage?: string;
  forceRefresh?: boolean;
  filters?: RemoteCatalogRequestFilters;
}) {
  const forceRefresh = options.forceRefresh === true;
  const errorMessage = options.errorMessage ?? 'Unable to load store items.';
  const listingCacheKey = buildRemoteCatalogListingCacheKey(options.limit, options.filters);
  const pageKey = `${listingCacheKey}:${options.offset}`;

  if (forceRefresh && options.offset === 0) {
    remoteCatalogListingCacheByLimit.delete(listingCacheKey);
    Array.from(remoteCatalogPagePromiseByKey.keys())
      .filter((key) => key.startsWith(`${listingCacheKey}:`))
      .forEach((key) => remoteCatalogPagePromiseByKey.delete(key));
  }

  const cachedListing = remoteCatalogListingCacheByLimit.get(listingCacheKey) ?? null;
  if (!forceRefresh && cachedListing?.loadedOffsets.includes(options.offset)) {
    return cachedListing;
  }

  if (!forceRefresh) {
    const cachedPromise = remoteCatalogPagePromiseByKey.get(pageKey);
    if (cachedPromise) {
      const page = await cachedPromise;
      return mergeRemoteCatalogListingCache(page);
    }
  }

  const nextRequest = fetchRemoteCatalogPage(options.limit, options.offset, errorMessage, options.filters);
  remoteCatalogPagePromiseByKey.set(pageKey, nextRequest);

  try {
    const page = await nextRequest;
    return mergeRemoteCatalogListingCache(page);
  } finally {
    if (remoteCatalogPagePromiseByKey.get(pageKey) === nextRequest) {
      remoteCatalogPagePromiseByKey.delete(pageKey);
    }
  }
}

export function findRemoteCatalogProductById(
  itemId: string | undefined,
  snapshot = remoteCatalogSnapshotCache
) {
  const normalizedItemId = typeof itemId === 'string' ? itemId.trim() : '';
  if (!normalizedItemId) {
    return null;
  }

  const snapshotMatch = snapshot
    ? findMatchingCatalogProduct(normalizedItemId, snapshot.adminProducts)
    : null;
  if (snapshotMatch) {
    return snapshotMatch;
  }

  const directCachedProduct = remoteCatalogProductCache.get(normalizedItemId);
  if (directCachedProduct) {
    return directCachedProduct;
  }

  return findMatchingCatalogProduct(normalizedItemId, Array.from(remoteCatalogProductCache.values()));
}
